import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import {
  FetchCompaniesRequest,
  FetchCompaniesResponse,
  ApiCompany,
} from "@/api/fetchCompanies/types";
import { syncCompanyFromSalesforce } from "@/api/syncCompanyFromSalesforce/syncCompanyFromSalesforceApi";
import {
  SyncCompanyFromSalesforceRequest,
  SyncCompanyFromSalesforceResponse,
} from "@/api/syncCompanyFromSalesforce/types";
import { getEditCompanyDetails } from "@/api/getEditCompanyDetails/getEditCompanyDetailsApi";
import {
  GetEditCompanyDetailsResponse,
  FieldServiceSoftware,
  Trade,
  CompanyEmployee,
  BusinessRoleOption,
} from "@/api/getEditCompanyDetails/types";
import { editCompany } from "@/api/editCompany/editCompanyApi";
import { EditCompanyDTO, EditCompanyResponse } from "@/api/editCompany/types";

export interface RemoteSyncLog {
  id: number;
  status: "pending" | "in_progress" | "success" | "failed";
  log: string;
  syncResults: {
    salesforce?: { request: unknown; response: unknown };
    memberportal?: { request: unknown; response: unknown };
    partnernetwork?: { request: unknown; response: unknown };
    scoreboard?: { request: unknown; response: unknown };
  } | null;
  startedAt: string | null;
  completedAt: string | null;
}

export interface Company {
  id: number;
  companyName: string;
  uuid: string;
  salesforceId?: string | null;
  intacctId?: string | null;
  zoomAccountId?: string | null;
  fathomAccountId?: string | null;
  marketingEnabled?: boolean;
  hasEaglesAccess?: boolean;
  hasLATAAccess?: boolean;
  companyEmail?: string | null;
  websiteUrl?: string | null;
  phoneNumber?: string | null;
  fieldServiceSoftwareId?: number | null;
  fieldServiceSoftwareName?: string | null;
  fieldServiceSoftwareList?: FieldServiceSoftware[];
  tradeIds?: number[]; // List of associated trade IDs
  tradeNames?: string[]; // Trade names for display
  primaryTradeName?: string | null;
  createdAt?: string | null;
  serviceTitanTenantId?: string | null;
  remoteSyncLog?: RemoteSyncLog | null;
  // Address fields
  billingStreet?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingPostalCode?: string | null;
  billingCountry?: string | null;
  shippingStreet?: string | null;
  shippingCity?: string | null;
  shippingState?: string | null;
  shippingPostalCode?: string | null;
  shippingCountry?: string | null;
  // Employees and business roles
  employees?: CompanyEmployee[];
  businessRoles?: BusinessRoleOption[];
}

interface CompaniesState {
  companies: Company[];
  selectedCompany: Company | null;
  tradeList: Trade[]; // Full list of trades
  companyTradeIds: number[]; // Trades associated with the company
  primaryTradeId: number | null; // Primary trade for the company
  totalCount: number;
  loading: boolean;
  error: string | null;
  updatingTrades: boolean;
  tradeError: string | null;
  syncingCompanyIds: number[]; // Companies currently being synced (for real-time updates)
}

const initialState: CompaniesState = {
  companies: [],
  selectedCompany: null,
  tradeList: [],
  companyTradeIds: [],
  primaryTradeId: null,
  totalCount: 0,
  loading: false,
  error: null,
  updatingTrades: false,
  tradeError: null,
  syncingCompanyIds: [],
};

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCompaniesData: (
      state,
      action: PayloadAction<{ companies: Company[]; totalCount: number }>,
    ) => {
      state.companies = action.payload.companies;
      state.totalCount = action.payload.totalCount;
    },
    setSelectedCompany: (state, action: PayloadAction<Company | null>) => {
      state.selectedCompany = action.payload;
    },
    updateCompany: (
      state,
      action: PayloadAction<Partial<Company> & { uuid: string }>,
    ) => {
      // Update selectedCompany if it matches the UUID
      if (
        state.selectedCompany &&
        state.selectedCompany.uuid === action.payload.uuid
      ) {
        state.selectedCompany = { ...state.selectedCompany, ...action.payload };
      }

      // Find and update the company in the companies array
      const index = state.companies.findIndex(
        (company) => company.uuid === action.payload.uuid,
      );
      if (index !== -1) {
        state.companies[index] = {
          ...state.companies[index],
          ...action.payload,
        };
      }
    },
    addCompany: (state, action: PayloadAction<Company>) => {
      state.companies.push(action.payload);
    },
    updateSelectedCompanyFieldServiceSoftware: (
      state,
      action: PayloadAction<{
        fieldServiceSoftwareId: number | null;
        fieldServiceSoftwareName: string | null;
      }>,
    ) => {
      if (state.selectedCompany) {
        state.selectedCompany.fieldServiceSoftwareId =
          action.payload.fieldServiceSoftwareId;
        state.selectedCompany.fieldServiceSoftwareName =
          action.payload.fieldServiceSoftwareName;
      }
    },
    setUpdatingTrades: (state, action: PayloadAction<boolean>) => {
      state.updatingTrades = action.payload;
    },
    setTradeError: (state, action: PayloadAction<string | null>) => {
      state.tradeError = action.payload;
    },
    updateCompanyTrades: (
      state,
      action: PayloadAction<{ tradeIds: number[] }>,
    ) => {
      if (state.selectedCompany) {
        state.selectedCompany.tradeIds = action.payload.tradeIds;
      }
      state.companyTradeIds = action.payload.tradeIds;
    },
    setTradeList: (state, action: PayloadAction<Trade[]>) => {
      state.tradeList = action.payload;
    },
    setCompanyTradeIds: (state, action: PayloadAction<number[]>) => {
      state.companyTradeIds = action.payload;
    },
    setPrimaryTradeId: (state, action: PayloadAction<number | null>) => {
      state.primaryTradeId = action.payload;
    },
    // Real-time sync tracking
    addSyncingCompany: (state, action: PayloadAction<number>) => {
      if (!state.syncingCompanyIds.includes(action.payload)) {
        state.syncingCompanyIds.push(action.payload);
      }
    },
    removeSyncingCompany: (state, action: PayloadAction<number>) => {
      state.syncingCompanyIds = state.syncingCompanyIds.filter(
        (id) => id !== action.payload,
      );
    },
    updateCompanySyncLog: (
      state,
      action: PayloadAction<{ companyId: number; syncLog: RemoteSyncLog }>,
    ) => {
      const { companyId, syncLog } = action.payload;
      const companyIndex = state.companies.findIndex((c) => c.id === companyId);
      if (companyIndex !== -1) {
        state.companies[companyIndex].remoteSyncLog = syncLog;
      }
    },
  },
});

export const {
  setLoading,
  setError,
  setCompaniesData,
  setSelectedCompany,
  updateCompany,
  addCompany,
  updateSelectedCompanyFieldServiceSoftware,
  setUpdatingTrades,
  setTradeError,
  updateCompanyTrades,
  setTradeList,
  setCompanyTradeIds,
  setPrimaryTradeId,
  addSyncingCompany,
  removeSyncingCompany,
  updateCompanySyncLog,
} = companiesSlice.actions;

// Thunks

export const fetchCompaniesAction =
  (requestData: FetchCompaniesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchCompaniesResponse =
        await fetchCompanies(requestData);

      const mappedCompanies: Company[] = response.data.companies.map(
        (apiCompany: ApiCompany): Company => ({
          id: apiCompany.id,
          companyName: apiCompany.companyName,
          uuid: apiCompany.uuid,
          salesforceId: apiCompany.salesforceId || null,
          intacctId: apiCompany.intacctId || null,
          marketingEnabled: apiCompany.marketingEnabled || false,
          hasEaglesAccess: apiCompany.hasEaglesAccess || false,
          hasLATAAccess: apiCompany.hasLATAAccess || false,
          remoteSyncLog: apiCompany.remoteSyncLog || null,
        }),
      );

      dispatch(
        setCompaniesData({
          companies: mappedCompanies,
          totalCount: response.meta?.totalCount || 0,
        }),
      );
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error ? error.message : "Failed to fetch companies",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const syncCompanyFromSalesforceAction =
  (
    requestData: SyncCompanyFromSalesforceRequest,
    callback?: (newCompany: Company) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: SyncCompanyFromSalesforceResponse =
        await syncCompanyFromSalesforce(requestData);
      const newCompanyFromApi = response.data;

      const newCompany: Company = {
        id: newCompanyFromApi.id,
        companyName: newCompanyFromApi.companyName,
        uuid: newCompanyFromApi.uuid,
        salesforceId: newCompanyFromApi.salesforceId || null,
        intacctId: newCompanyFromApi.intacctId || null,
      };

      dispatch(addCompany(newCompany));

      if (callback) {
        callback(newCompany);
      }
    } catch (error: unknown) {
      // Extract error message from API response
      let errorMessage = "Failed to sync company from Salesforce";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; message?: string } };
        };
        errorMessage =
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchEditCompanyDetailsAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: GetEditCompanyDetailsResponse =
        await getEditCompanyDetails(uuid);

      const companyDetails: Company = {
        id: response.data.id,
        companyName: response.data.companyName,
        uuid: uuid,
        salesforceId: response.data.salesforceId,
        intacctId: response.data.intacctId,
        zoomAccountId: response.data.zoomAccountId,
        fathomAccountId: response.data.fathomAccountId,
        marketingEnabled: response.data.marketingEnabled,
        hasEaglesAccess: response.data.hasEaglesAccess,
        hasLATAAccess: response.data.hasLATAAccess,
        companyEmail: response.data.companyEmail,
        websiteUrl: response.data.websiteUrl,
        phoneNumber: response.data.phoneNumber,
        fieldServiceSoftwareId: response.data.fieldServiceSoftwareId,
        fieldServiceSoftwareName: response.data.fieldServiceSoftwareName,
        fieldServiceSoftwareList: response.data.fieldServiceSoftwareList,
        tradeIds: response.data.companyTradeIds || [],
        tradeNames: response.data.tradeNames || [],
        primaryTradeName: response.data.primaryTradeName,
        // Billing address
        billingStreet: response.data.billingStreet,
        billingCity: response.data.billingCity,
        billingState: response.data.billingState,
        billingPostalCode: response.data.billingPostalCode,
        billingCountry: response.data.billingCountry,
        // Shipping address
        shippingStreet: response.data.shippingStreet,
        shippingCity: response.data.shippingCity,
        shippingState: response.data.shippingState,
        shippingPostalCode: response.data.shippingPostalCode,
        shippingCountry: response.data.shippingCountry,
        // Employees and business roles
        employees: response.data.employees || [],
        businessRoles: response.data.businessRoles || [],
      };

      dispatch(setSelectedCompany(companyDetails));
      dispatch(setTradeList(response.data.tradeList || []));
      dispatch(setCompanyTradeIds(response.data.companyTradeIds || []));
      dispatch(setPrimaryTradeId(response.data.primaryTradeId ?? null));
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch company details",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const editCompanyAction =
  (
    uuid: string,
    editCompanyDTO: EditCompanyDTO,
    callback?: (updatedCompany: Company) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: EditCompanyResponse = await editCompany(
        uuid,
        editCompanyDTO,
      );

      // Only update the editable fields
      dispatch(
        updateSelectedCompanyFieldServiceSoftware({
          fieldServiceSoftwareId: response.data.fieldServiceSoftwareId,
          fieldServiceSoftwareName: response.data.fieldServiceSoftwareName,
        }),
      );

      const updatedCompany: Company = {
        id: 0,
        companyName: response.data.companyName,
        uuid: uuid,
        salesforceId: response.data.salesforceId,
        intacctId: response.data.intacctId,
        zoomAccountId: response.data.zoomAccountId,
        fathomAccountId: response.data.fathomAccountId,
        marketingEnabled: response.data.marketingEnabled,
        hasEaglesAccess: response.data.hasEaglesAccess || false,
        hasLATAAccess: response.data.hasLATAAccess || false,
        companyEmail: response.data.companyEmail,
        websiteUrl: response.data.websiteUrl,
        fieldServiceSoftwareId: response.data.fieldServiceSoftwareId,
        fieldServiceSoftwareName: response.data.fieldServiceSoftwareName,
        tradeIds: response.data.tradeIds || [],
      };

      dispatch(updateCompany(updatedCompany));
      dispatch(setCompanyTradeIds(response.data.tradeIds || []));

      if (callback) {
        const updatedCompany: Company = {
          id: 0,
          companyName: response.data.companyName,
          uuid: uuid,
          fieldServiceSoftwareId: response.data.fieldServiceSoftwareId,
          fieldServiceSoftwareName: response.data.fieldServiceSoftwareName,
        };
        callback(updatedCompany);
      }
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to edit company details",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default companiesSlice.reducer;
